import { AnchorType, ItemState } from '../../../constants';
import { shapeNames } from '../../../shapes/constants';
import {
  deleteUtils,
  getAnchorGroupId,
  getAnchorPoints,
  getNodeTitle,
  parseAnchors,
  updateShapeText,
} from '../../../shapes/utils';
import { Group, NodeModel, ShapeElement } from '../../../types';
import { getNodeDefaultOptions } from './base.styles';
import { BaseNode } from './base.types';
import {
  drawAnchors,
  drawErrorHighlight,
  drawNode,
  drawNodeId,
  drawNoteIndicator,
  getMinHeightNeeded,
  getMinWidthNeeded,
  getNodeHeight,
  getNodeIdText,
  getNodeWidth,
  getUpdatedShapeAttrs,
  numUsersTotalUtil,
  numUsersUtil,
  updateAnchors,
  updateDescBottom,
  updateErrorHighlight,
  updateNoteIndicator,
} from './baseUtils';

const defaultOptions = getNodeDefaultOptions();

// @ts-ignore
export const baseNodeConfig: BaseNode = {
  options: defaultOptions,
  size: defaultOptions.size,

  /* --------------------------------- GETTERS -------------------------------- */
  getCustomConfig(model) {
    return model;
  },

  getCurrentSize() {
    return this.size;
  },

  getWidth(group, useCache = true): number {
    return useCache ? this.size[0] : getNodeWidth(group);
  },

  getHeight(group, useCache = true): number {
    return useCache ? this.size[1] : getNodeHeight(group);
  },

  getMinWidthNeeded({ group, options, model }) {
    return getMinWidthNeeded({ group, options, model });
  },

  getMinHeightNeeded({ options }) {
    return getMinHeightNeeded({ options });
  },

  getRequiredWidth(model, group, options) {
    return this.getMinWidthNeeded({ group, options: options || this.getOptions(model), model });
  },

  getRequiredHeight(model, group, options) {
    return this.getMinHeightNeeded({ group, options: options || this.getOptions(model), model });
  },

  getRequiredSize(model, group, options) {
    const nodeOptions = options || this.getOptions(model);
    return [
      this.getRequiredWidth(model, group, nodeOptions),
      this.getRequiredHeight(model, group, nodeOptions),
    ];
  },

  getAnchorPoints(model) {
    const { outputConnectors = [], inputConnectors = [] } = model;

    return [...parseAnchors(inputConnectors, 0), ...parseAnchors(outputConnectors, 1)];
  },

  getBaseShape(group) {
    return group.findByClassName(shapeNames.wrapper);
  },

  /* ------------------------------- DRAW UTILS ------------------------------- */

  draw(model, group) {
    if (!model || !group) return;
    const { isInProgressTotalPublishedView } = model || {};

    const options = this.getOptions(model);
    const size = this.getRequiredSize(model, group, options);

    this.size = size;

    drawErrorHighlight({ size, options, group, model });
    drawNoteIndicator({ options, group, model });

    // The keyshape is the outmost shape of the node, and where
    // node events stem from
    // https://g6.antv.vision/en/docs/manual/middle/elements/shape/shape-keyshape/#keyshape
    const keyShape = drawNode({ size, options, group, model });

    drawNodeId({ size, options, group, model });
    if (!!isInProgressTotalPublishedView) {
      numUsersTotalUtil.draw(numUsersTotalUtil.TotalUserType.IN_PROGRESS, {
        size,
        options,
        group,
        model,
      });
      numUsersTotalUtil.draw(numUsersTotalUtil.TotalUserType.ENTERED, {
        size,
        options,
        group,
        model,
      });
      numUsersTotalUtil.draw(numUsersTotalUtil.TotalUserType.EXITED, {
        size,
        options,
        group,
        model,
      });
    } else {
      numUsersUtil.draw({ size, options, group, model });
    }

    drawAnchors({ size, options, group, model });

    if (deleteUtils.isEnabled(model)) {
      deleteUtils.draw({ size, options, group, model });
    }

    this.afterBaseDraw({ options, size, group, model, keyShape });

    group.sort();

    return keyShape;
  },

  afterBaseDraw() {},

  /* ------------------------------ UPDATE UTILS ------------------------------ */

  update(model, item) {
    const { isInProgressTotalPublishedView } = model || {};
    const group = item.getContainer();
    const options = this.getOptions(model);
    const size = this.getRequiredSize(model, group, options);

    updateErrorHighlight({ size, group, model, options });
    updateNoteIndicator({ group, model });

    // update node id text
    updateShapeText({ className: shapeNames.nodeId, group, text: getNodeIdText(model) });
    // update node title text
    updateShapeText({ className: shapeNames.title, group, text: getNodeTitle(model, options) });

    updateDescBottom({ options, group, model, size });
    updateAnchors({ options, group, model, size });

    if (isInProgressTotalPublishedView) {
      numUsersTotalUtil.update(numUsersTotalUtil.TotalUserType.IN_PROGRESS, {
        size,
        group,
        model,
        options,
      });
      numUsersTotalUtil.update(numUsersTotalUtil.TotalUserType.ENTERED, {
        size,
        group,
        model,
        options,
      });
      numUsersTotalUtil.update(numUsersTotalUtil.TotalUserType.EXITED, {
        size,
        group,
        model,
        options,
      });
    } else {
      numUsersUtil.update({ size, group, model, options });
    }

    this.updateSizeIfNeeded({ model, group, requiredSize: size, options });

    /**
     * https://iterable.atlassian.net/browse/WF-516
     *
     * React rerendering resets all states.  This is because `graph.changeData`
     * (called when the component updates) clears all states.
     * To persist the selected state after a rerender, we have to manually
     * reset it after the component is updated.
     *
     * It would be preferable to get the selected nodes from a `FlowChart`
     * prop instead as it would eliminate the need to loop through every node
     * to add `isSelected`, but I'm having issues accessing it from here :(
     *
     * TODO: Test with large amount of nodes during WF-356.
     */
    if (model?.isSelected) {
      item.setState(ItemState.Selected, true);
      item.toFront();
    }

    if (model?.isAIGenerated) {
      item.setState(ItemState.AIGenerated, true);
    }

    this.afterBaseUpdate({ options, size, group, model, item });

    group.sort();
  },

  afterBaseUpdate() {},

  updateHeight(height, model, group, options) {
    this.size[1] = height;

    const [width] = this.size;
    const wrapper = group.findByClassName(shapeNames.wrapper);

    if (!wrapper || (wrapper as ShapeElement)?.attrs?.height === height) return;

    wrapper.attr('height', height);

    // Update Anchor Positions
    const anchorGroupHeight = height - 10;
    const { outputs = [] } = getAnchorPoints(model);
    outputs.forEach(([, , text], i) => {
      const anchorGroup = group.findById(getAnchorGroupId(AnchorType.Out, i));
      if (!anchorGroup) return;
      const anchor = (anchorGroup as Group).findByClassName(shapeNames.anchorOut);
      if (!anchor) return;

      anchorGroup.attr('y', anchorGroupHeight);
      anchor.attr('y', height);

      if (text) {
        const anchorTitle = (anchorGroup as Group).findByClassName(shapeNames.anchorTitle);
        if (!anchorTitle) return;
        anchorTitle.attr('y', anchorGroupHeight);
      }
    });

    updateErrorHighlight({ size: [width, height], group, model, options });
    this.afterBaseHeightUpdate({ options, width, group, model, height });
  },

  afterBaseHeightUpdate() {},

  updateWidth(width, model, group, options) {
    const { isInProgressTotalPublishedView } = model || {};
    this.size[0] = width;
    /**
     * NOTE: On initial render, a bunch of calculations are done when drawing
     * some of these, and then those calculations are immediately redone after
     * draw.  This should be updated so that the calculations are only done once.
     * Biggest culprits: anchors, numUsers
     */
    const wrapper = group.findByClassName(shapeNames.wrapper);

    if (!wrapper || (wrapper as ShapeElement)?.attrs?.width === width) return;

    wrapper.attr('width', width);

    // Update delete icon position
    if (deleteUtils.isEnabled(model)) {
      deleteUtils.updatePosition({ group, size: this.size, options, model });
    }

    if (!!isInProgressTotalPublishedView) {
      // Update numUsers position
      numUsersTotalUtil.updatePosition(numUsersTotalUtil.TotalUserType.IN_PROGRESS, {
        group,
        size: this.size,
        options,
      });
      numUsersTotalUtil.updatePosition(numUsersTotalUtil.TotalUserType.ENTERED, {
        group,
        size: this.size,
        options,
      });
      numUsersTotalUtil.updatePosition(numUsersTotalUtil.TotalUserType.EXITED, {
        group,
        size: this.size,
        options,
      });
    } else {
      numUsersUtil.updatePosition({ group, size: this.size, options });
    }

    // Update Anchor Positions
    const { outputs = [] } = getAnchorPoints(model);
    outputs.forEach(([xFraction, , text], i) => {
      const x = width * xFraction;
      const anchorGroup = group.findById(getAnchorGroupId(AnchorType.Out, i));
      if (!anchorGroup) return;
      const anchor = (anchorGroup as Group).findByClassName(shapeNames.anchorOut);
      if (!anchor) return;

      anchor.attr('x', x);

      if (text) {
        const anchorTitle = (anchorGroup as Group).findByClassName(shapeNames.anchorTitle);
        anchorTitle.attr('x', x);
      }
    });

    // We can to default this to `true`, hence the `!== false` weirdness
    // https://github.com/Iterable/iterable-frontend/pull/2648#discussion_r647803476
    const isDescBottomTruncated =
      model?.nodeData?.textSegments?.nodeDescBottom?.isTruncated !== false;

    if (isDescBottomTruncated) {
      updateDescBottom({ options, group, model, size: this.size });
    }

    this.afterBaseWidthUpdate({ options, width, group, model, height: this.size[1] });
  },

  afterBaseWidthUpdate() {},

  updateSizeIfNeeded({ model, group, requiredSize, options: nodeOptions }) {
    if (!Array.isArray(requiredSize)) return;

    const options = nodeOptions || this.getOptions(model);
    const [, height] = requiredSize;

    this.updateWidthIfNeeded({ model, group, options });
    this.updateHeightIfNeeded({ model, group, options, height });
  },

  updateWidthIfNeeded({ model, group, options }) {
    const nodeOptions = options || this.getOptions(model);
    const requiredWidth = this.getRequiredWidth(model as NodeModel, group, nodeOptions);

    if (this.getWidth(group, false) !== requiredWidth) {
      this.updateWidth(requiredWidth, model as NodeModel, group, nodeOptions);
    }
  },

  updateHeightIfNeeded({ model, group, height, options }) {
    const nodeOptions = options || this.getOptions(model);
    const requiredHeight = height || this.getRequiredHeight(model as NodeModel, group, nodeOptions);

    if (this.getHeight(group, false) !== requiredHeight) {
      this.updateHeight(requiredHeight, model as NodeModel, group, nodeOptions);
    }
  },

  /* ------------------------------- STATE UTILS ------------------------------ */

  setState(name, value, item) {
    const group = item.getContainer();
    const model = item.getModel();
    const states = item.getStates();
    const options = this.getOptions(model);

    // For performance reasons, I'm currently not watching every class
    [
      shapeNames.wrapper,
      shapeNames.icon,
      shapeNames.title,
      shapeNames.numUsersWrapper,
      shapeNames.numUsersEnteredWrapper,
      shapeNames.numUsersExitedWrapper,
    ].forEach((className) => {
      const newAttrs = getUpdatedShapeAttrs({ className, options, states });
      const shape = group.findByClassName(className);

      if (shape) {
        shape.attr(newAttrs);
      }
    });

    this.afterSetState(name, value, item);
  },

  afterSetState(name, value, item) {
    const model = item.getModel();

    if (deleteUtils.isEnabled(model as NodeModel)) {
      const group = item.getContainer();

      if (name === ItemState.Active) {
        deleteUtils.updateOpacity({ group, isVisible: !!value });
      } else {
        deleteUtils.updateVisibility({ group, isVisible: false });
      }
    }
  },
};
